// src/modules/requerimiento/pages/RequerimientoPage.jsx
import React, { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";

import {
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useAuth } from "../../../context/AuthContext";

// === Pestaña 0 (tu flujo existente)
import SearchBar from "../components/SearchBar";
import BusquedasRecientes, { useBusquedasRecientes } from "../components/BusquedasRecientes";
import ClientsTable from "../components/ClientsTable";
import RequerimientoModal from "../components/RequerimientoModal";
import { useClientesYEquipos } from "../hooks/useClientesYEquipos";
import { usePlataformasYTecnicos } from "../hooks/usePlataformasYTecnicos";

import RequerimientoListado from "../components/RequerimientoListado";
import RequerimientoReporte from "../components/RequerimientoReporte";
import CalendarioFull from "../../calendario/components/CalendarioFull";
import RequerimientoDetalle from "./RequerimientoDetalle";

// 🔹 NUEVA IMPORTACIÓN: Mis Pendientes
import RequerimientoMisPendientes from "../views/RequerimientoMisPendientes";

// 🔹 Importar HistorialCliente para renderizarlo dentro del TabPanel
import HistorialCliente from "../../historialCliente/pages/HistorialCliente";
// 🔹 Vista "Plataformas" (TRS Rastreo / Wialon / Leasecontrol → Usuarios/Unidades/Grupos)
import ClientePlataformasResumen from "../../panelGlobal/components/ClientePlataformasResumen";

const normalizarSucursal = (value) => {
  const texto = String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  if (texto.includes("santa cruz")) return "Santa Cruz";
  if (texto.includes("cochabamba")) return "Cochabamba";
  if (texto.includes("la paz") || texto.includes("el alto")) return "La Paz";
  return "";
};

const a11yProps = (index) => ({
  id: `tab-${index}`,
  "aria-controls": `tabpanel-${index}`,
});

const TabPanel = ({ children, active, id }) => (
  <Box
    role="tabpanel"
    hidden={!active}
    id={`tabpanel-${id}`}
    aria-labelledby={`tab-${id}`}
    sx={{ display: active ? "block" : "none", pt: 1 }}
  >
    {children}
  </Box>
);

export default function RequerimientoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const sucursalUsuario = normalizarSucursal(user?.sucursal) || "La Paz";

  // tabs + modal
  const initialTab = Number(location.state?.tab ?? 0);
  const [tab, setTab] = useState(initialTab);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // 🔹 Navegación interna dentro de la pestaña 0 (sin Dialog, sin columnas)
  const [view0, setView0] = useState("list"); // "list" | "historial" | "plataformas"
  const [histCli, setHistCli] = useState(null);
  const [viewListado, setViewListado] = useState("list"); // "list" | "detalle"
  const [reqDetalle, setReqDetalle] = useState(null);
  const [viewReporte, setViewReporte] = useState("list"); // "list" | "detalle"
  const [reporteDetalle, setReporteDetalle] = useState(null);

  // hooks de la pestaña "Emitir Requerimiento"
  const {
    clientes,
    equiposMap,
    servicios,
    sucursal,
    loadingServicios,
    searching,
    searchPerformed,
    page,
    rowsPerPage,
    totalClientes,
    search,
    changePage,
    changeRowsPerPage,
    updateClienteLocal,
  } =
    useClientesYEquipos();
  const { recientes, precargar, buscar, repetir, quitar, limpiar } = useBusquedasRecientes("requerimiento_busquedas_recientes", search);
  const { plataformas, tecnicos, fetchPlataformas, fetchTecnicos } =
    usePlataformasYTecnicos();

  const handleOpenModal = (cli) => {
    setSelected(cli);
    setModalOpen(true);
  };

  const handleClienteUpdated = useCallback((codServicioCliente, patch) => {
    updateClienteLocal(codServicioCliente, patch);
    setSelected((prev) =>
      String(prev?.cod_servicio_cliente) === String(codServicioCliente)
        ? { ...prev, ...patch }
        : prev
    );
  }, [updateClienteLocal]);

  const openHistorial = (cli) => {
    setHistCli(cli);
    setView0("historial");
  };

  // Boton "Plataformas" de ClientsTable — vista propia con las 3 plataformas de
  // rastreo del cliente (TRS Rastreo / Wialon / Leasecontrol), cada una con sus
  // Usuarios / Unidades / Grupos. Historial de Cliente conserva sus 3 botones aparte.
  const openPlataformas = (cli) => {
    setHistCli(cli);
    setView0("plataformas");
  };

  const backToList = () => {
    setHistCli(null);
    setView0("list");
  };

  const openReqDetalle = (req) => {
    setReqDetalle(req);
    setViewListado("detalle");
  };

  const backToReqListado = () => {
    setReqDetalle(null);
    setViewListado("list");
  };

  const openReporteDetalle = (req) => {
    setReporteDetalle(req);
    setViewReporte("detalle");
  };

  const backToReporteList = () => {
    setReporteDetalle(null);
    setViewReporte("list");
  };

  return (
    <DashboardLayout>
      <Box sx={{ width: "100%" }}>
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden", borderRadius: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 34,
              "& .MuiTabs-indicator": {
                height: 2,
                bgcolor: "primary.main",
              },
              "& .MuiTab-root": {
                minHeight: 34,
                py: 0.5,
                px: 1.25,
                fontSize: 12,
                fontWeight: 700,
                textTransform: "none",
                color: "text.secondary",
              },
              "& .Mui-selected": {
                color: (theme) =>
                  `${theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : theme.palette.primary.main} !important`,
              },
            }}
          >
            <Tab label="Emitir Requerimiento" {...a11yProps(0)} />
            <Tab label="Listado Requerimiento" {...a11yProps(1)} />
            <Tab label="Calendario" {...a11yProps(2)} />
            <Tab label="Mis pendientes" {...a11yProps(3)} />
            <Tab label="Reporte" {...a11yProps(4)} />
          </Tabs>
        </Paper>

        {/* ===== Pestaña 0 */}
        <TabPanel active={tab === 0} id={0}>
          {view0 === "list" ? (
            <>
              <SearchBar
                onSearch={buscar}
                precargar={precargar}
                searching={searching}
                servicios={servicios}
                loadingServicios={loadingServicios}
                sucursalDefault={sucursal}
              />

              {!searchPerformed ? (
                <BusquedasRecientes titulo="Busca un cliente para emitir su requerimiento" recientes={recientes} servicios={servicios} onRepetir={repetir} onQuitar={quitar} onLimpiar={limpiar} />
              ) : searching ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography>Buscando datos…</Typography>
                </Box>
              ) : clientes.length === 0 ? (
                <Typography>No se encontraron clientes.</Typography>
              ) : (
                <ClientsTable
                  clientes={clientes}
                  equiposMap={equiposMap}
                  onEmitirReq={handleOpenModal}
                  onOpenHistorial={openHistorial}
                  onOpenPlataformas={openPlataformas}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  total={totalClientes}
                  onPageChange={changePage}
                  onRowsPerPageChange={changeRowsPerPage}
                />
              )}
            </>
          ) : view0 === "historial" ? (
            <Box>
              {/* Header simple para volver (sin afectar menú/tabs) */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton onClick={backToList} title="Volver">
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                  Historial — {histCli?.nombre_cliente || ""} ({histCli?.cod_servicio_cliente || ""})
                </Typography>
              </Stack>

              <HistorialCliente
                codServicioClienteProp={histCli?.cod_servicio_cliente}
                embedded
                onBack={backToList}
              />
            </Box>
          ) : (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton onClick={backToList} title="Volver">
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontSize: { xs: 16, md: undefined }, lineHeight: 1.25, minWidth: 0 }}>
                  Plataformas — {histCli?.nombre_cliente || ""} ({histCli?.cod_servicio_cliente || ""})
                </Typography>
              </Stack>

              <ClientePlataformasResumen codServicioCliente={histCli?.cod_servicio_cliente} />
            </Box>
          )}
        </TabPanel>

        {/* ===== Pestaña 1 */}
        <TabPanel active={tab === 1} id={1}>
          {viewListado === "list" ? (
            <RequerimientoListado onOpenDetalle={openReqDetalle} />
          ) : (
            <RequerimientoDetalle
              codProp={reqDetalle?.cod}
              embedded
              onBack={backToReqListado}
            />
          )}
        </TabPanel>

        {/* ===== Pestaña 2: Calendario */}
        <TabPanel active={tab === 2} id={2}>
          <CalendarioFull />
        </TabPanel>

        {/* ===== Pestaña 3: Mis pendientes */}
        <TabPanel active={tab === 3} id={3}>
          <RequerimientoMisPendientes />
        </TabPanel>

        {/* ===== Pestaña 4: Reporte */}
        <TabPanel active={tab === 4} id={4}>
          {viewReporte === "list" ? (
            <RequerimientoReporte onOpenDetalle={openReporteDetalle} />
          ) : (
            <RequerimientoDetalle
              codProp={reporteDetalle?.cod}
              embedded
              onBack={backToReporteList}
            />
          )}
        </TabPanel>
      </Box>

      {/* Modal (pestaña 0) */}
      <RequerimientoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selected={selected}
        sucursalDefault={sucursalUsuario}
        plataformas={plataformas}
        fetchPlataformas={fetchPlataformas}
        tecnicos={tecnicos}
        fetchTecnicos={fetchTecnicos}
        onClienteUpdated={handleClienteUpdated}
        onSuccessNavigate={(cod) => navigate(`/requerimiento/${cod}`)}
      />
    </DashboardLayout>
  );
}
